// @ts-check
import { useEffect, useState } from "react";
import useZeroSelector from '~/hooks/useZeroSelector';

import {
    kiosk_get_activation_keys,
    kiosk_get_sessions,
    kiosk_turn_off,
    kiosk_update_activation_key,
} from '~/api/zero-api';
import { dateFormatterNoTime, mainContentContainerClass, safe_get, scrollToTop } from '~/other/Helper';

import Skeleton from "react-loading-skeleton";
import copy from "copy-to-clipboard";

import { DeleteOutlined, EditOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { Dropdown, Tag } from "antd";

import NotificationAlert from '~/other/NotificationAlert';
import ConfirmModal from '~/components/Shared/ConfirmModal';
import NewEditKioskModal from '~/components/AdminPages/ManageKiosks/NewEditKioskModal';

import Button from '~/components/Shared/Button';
import { IoIosMore as MoreIcon } from "react-icons/io";
import KioskLinkModal from '~/components/AdminPages/ManageKiosks/KioskLinkModal';
import QRCode from '~/components/Shared/QRCode';
import ButtonLink from '~/components/Shared/ButtonLink';
import { Link } from "react-router-dom";
import useOrganizationId from '~/hooks/useOrganizationId';
import { getKioskActivationLink } from '~/other/Helper';

export default function KioskModeSettings() {
    const orgId = useOrganizationId();

    const [loading, setLoading] = useState(true);
    const [kioskSessions, setKioskSessions] = useState([]);
    /** @type {useState<KioskActivationKey | null>} */
    const [kioskActivationKey, setKioskActivationKey] = useState(null);
    const [modalState, setModalState] = useState(null);

    const kioskMode = useZeroSelector((state) => safe_get(state, "app.kiosk_mode", false));

    useEffect(() => {
        scrollToTop("page-head");
        Promise.allSettled([getKioskSessions(), getKioskActivationKeys()]).then(() => {
            setLoading(false);
        });
    }, []);

    const getKioskSessions = async () => {
        try {
            const response = await kiosk_get_sessions();
            const data = await response.json();
            const kioskSessions = safe_get(data, "sessions", []);
            setKioskSessions(kioskSessions);
        } catch (error) {
            NotificationAlert("error", "", "Unable to fetch kiosk sessions.");
        }
    };

    const getKioskActivationKeys = async () => {
        try {
            const response = await kiosk_get_activation_keys();
            const kioskActivationKeys = await response.json();
            if (kioskActivationKeys.length > 0) {
                setKioskActivationKey(kioskActivationKeys[0]);
            } else {
                setKioskActivationKey(null);
            }
        } catch (error) {
            NotificationAlert("error", "", "Unable to fetch kiosk activation keys.");
        }
    };

    const deviceNameFormatter = (device) => {
        return <span className="zero-dark-grey">{device.device_name}</span>;
    };

    const statusFormatter = (device) => {
        if (device.aged) {
            return <Tag style={{ color: "#505050" }}>Inactive &gt; 30 days</Tag>;
        } else {
            return (
                <Tag color="green" style={{ borderColor: "#52c41a" }}>
                    Active
                </Tag>
            );
        }
    };

    const optionsFormatter = (device) => {
        const menu = {
            onClick: (e) => handleMenuClick(e.key, device),
            items: [
                {
                    key: "edit",
                    icon: <EditOutlined />,
                    label: "Edit Kiosk",
                },
                {
                    type: "divider",
                },
                {
                    key: "delete",
                    label: "Delete Kiosk",
                    icon: <DeleteOutlined />,
                    className: "zero-delete-red",
                },
            ],
        };

        return (
            <div
                className="media-right more"
                style={{ marginTop: "6px", color: "grey", lineHeight: 0, height: "fit-content" }}
            >
                <Dropdown
                    // @ts-ignore
                    menu={menu}
                    trigger={["click"]}
                >
                    <button className="ButtonLink ant-dropdown-link">
                        <MoreIcon style={{ height: "20px", width: "20px" }} />
                    </button>
                </Dropdown>
            </div>
        );
    };

    const handleMenuClick = (key, device) => {
        if (key === "edit") {
            setModalState({ key: "editKiosk", obj: device });
        } else if (key === "delete") {
            setModalState({ key: "deleteSession", obj: device });
        }
    };

    const removeSession = () => {
        var kioskDevices = [...kioskSessions];
        var index = kioskDevices.indexOf(modalState.obj);
        var is_current_session = modalState.obj.is_current;

        kiosk_turn_off(modalState.obj.session_uuid).then(
            function (success) {
                if (index >= 0) {
                    kioskDevices.splice(index, 1);
                    setModalState(null);
                    setKioskSessions(kioskDevices);
                    getKioskSessions();
                    NotificationAlert("success", "Success!", "Kiosk has been turned off.");
                }

                if (is_current_session) {
                    window.location.href = "/login";
                }
            },
            function (error) {
                setModalState(null);
                NotificationAlert("error", "Oops!", "An Error occurred. Unable to turn off kiosk.");
            }
        );
    };

    const updateKioskLinkStatus = async (kioskActivationKey) => {
        try {
            const response = await kiosk_update_activation_key(kioskActivationKey.uuid, {
                deactivated: !kioskActivationKey.deactivated,
            });
            const data = await response.json();
            setKioskActivationKey(data);
            NotificationAlert("success", "Success!", "Kiosk link status updated.");
        } catch (error) {
            NotificationAlert("error", "", "Unable to update kiosk link status.");
        } finally {
            setModalState(null);
        }
    };

    return (
        <div className={mainContentContainerClass()}>
            {modalState?.key === "editKiosk" && (
                <NewEditKioskModal
                    show={true}
                    cancel={() => setModalState(null)}
                    editKiosk={modalState.obj}
                    refresh={getKioskSessions}
                />
            )}

            {modalState?.key === "deleteSession" && (
                <ConfirmModal
                    show={true}
                    cancel={() => setModalState(null)}
                    confirm={removeSession}
                    title={"Confirmation"}
                    body={"Are you sure you want to delete this Kiosk?"}
                    confirmButtonName={"Delete Kiosk"}
                />
            )}

            {(modalState?.key === "createKioskLink" || modalState?.key === "editKioskLink") && (
                <KioskLinkModal
                    existingLink={kioskActivationKey}
                    cancel={() => setModalState(null)}
                    onSuccess={(activationKey) => {
                        setKioskActivationKey(activationKey);
                        setModalState(null);
                    }}
                />
            )}

            {modalState?.key === "kioskLinkStatusUpdate" && (
                <ConfirmModal
                    show={true}
                    cancel={() => setModalState(null)}
                    confirm={() => {
                        updateKioskLinkStatus(modalState.obj);
                    }}
                    title={"Confirmation"}
                    body={`Are you sure you want to ${
                        modalState.obj.deactivated ? "enable" : "disable"
                    } this kiosk link?`}
                    confirmButtonName={modalState.obj.deactivated ? "Enable Kiosk Link" : "Disable Kiosk Link"}
                />
            )}

            <div id="page-head" className="no-padding-mobile">
                <div className="row">
                    <div className="col-lg-12  col-xs-12 col-xs-offset-0">
                        <div id="page-title" style={{ padding: "0px" }}>
                            <div className="panel thin-border" style={{ maxWidth: "var(--max-desktop-panel-width)", margin: "0 auto" }}>
                                <div className="nav-header-panel no-padding-print">
                                    <div className="team-name-header">
                                        <h3
                                            className={"section-titles admin-page-header"}
                                            style={{ display: "inline-block", margin: "0px" }}
                                        >
                                            Manage Kiosks
                                        </h3>
                                    </div>
                                    <p
                                        className="header"
                                        style={{ color: "#505050", marginBottom: "0px", paddingLeft: "2px" }}
                                    >
                                        Kiosk Mode allows multiple users to login from a shared device. There are two ways to activate Kiosk Mode.{" "}
                                        <a
                                            rel="noopener noreferrer"
                                            onClick={() =>
                                                // @ts-ignore
                                                window.FreshworksWidget("open", "article", { id: 70000416835 })
                                            }
                                            className="blue-link"
                                        >
                                            Learn more
                                        </a>
                                        .
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div id="page-content" className="no-padding-mobile">
                <div className="panel thin-border zero-blue pad-10">
                    <div className="flex flex-space-between" style={{ alignItems: "flex-start" }}>
                        <h4 className="zero-blue">Kiosk Link</h4>
                        {kioskActivationKey ? (
                            <Link
                                to={`/${orgId}/home/kiosk_link/${kioskActivationKey.uuid}/${kioskActivationKey.activation_key}`}
                            >
                                <Button type="primary">Print QR Code</Button>
                            </Link>
                        ) : (
                            <Button type="primary" onClick={() => setModalState({ key: "createKioskLink" })}>
                                <PlusCircleOutlined /> Create Kiosk Link
                            </Button>
                        )}
                    </div>
                    <p className="zero-dark-grey">
                        A kiosk link is the simplest and fastest way to activate and use Kiosk Mode. However, please be aware 
                        that kiosk links can be used by anyone that receives the link. For added security, we recommend
                        requiring an activation code (optional), changing the activation code periodically, and only
                        sharing kiosk links with intended recipients.
                    </p>
                    {kioskActivationKey && (
                        <KioskActivationLinkDetails
                            kioskActivationKey={kioskActivationKey}
                            onEdit={(kioskActivationKey) =>
                                setModalState({ key: "editKioskLink", obj: kioskActivationKey })
                            }
                            onStatusUpdate={(kioskActivationKey) =>
                                setModalState({ key: "kioskLinkStatusUpdate", obj: kioskActivationKey })
                            }
                        />
                    )}
                </div>
                <div className="panel thin-border zero-blue pad-10">
                    <div className="flex flex-space-between" style={{ alignItems: "flex-start" }}>
                        <h4 className="zero-blue">Kiosks</h4>
                        <Button
                            type="primary"
                            onClick={() => setModalState({ key: "editKiosk", obj: undefined })}
                            disabled={kioskMode}
                        >
                            <PlusCircleOutlined /> Create Kiosk
                        </Button>
                    </div>

                    <div
                        className="table-responsive"
                        style={{
                            maxWidth: "var(--max-desktop-panel-width)",
                            margin: "0 auto",
                        }}
                    >
                        <table className="table table-vcenter" style={{ marginBottom: "0px" }}>
                            <thead style={{ color: "#1D2A35" }}>
                                <tr>
                                    <th></th>
                                    <th className="zero-blue">Kiosk Name</th>
                                    <th className="zero-blue">Created On</th>
                                    <th className="zero-blue">Last Access</th>
                                    <th className="zero-blue">Status</th>
                                </tr>
                            </thead>
                            {loading && (
                                <tbody style={{ borderTop: "0px" }}>
                                    {[...Array(8)].map((_item, index) => (
                                        <tr key={index} className="tr-hover">
                                            <td>
                                                <Skeleton width={25} />
                                            </td>
                                            <td className="text-left">
                                                <Skeleton width={100} />
                                            </td>
                                            <td className="text-left">
                                                <Skeleton width={100} />
                                            </td>
                                            <td className="text-left">
                                                <Skeleton width={100} />
                                            </td>
                                            <td className="text-left">
                                                <Skeleton width={60} height={22} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            )}
                            {!loading && (
                                <tbody style={{ borderTop: "0px" }}>
                                    {kioskSessions.map((device, index) => (
                                        <tr
                                            key={index}
                                            className={
                                                "tr-hover" +
                                                (safe_get(device, "access_time", "").toLowerCase() === "current session"
                                                    ? " tr-current-session"
                                                    : "")
                                            }
                                        >
                                            <td>{optionsFormatter(device)}</td>
                                            <td className="text-left zero-dark-grey">{deviceNameFormatter(device)}</td>
                                            <td className="text-left zero-dark-grey">
                                                {dateFormatterNoTime(device.created_at)}
                                            </td>
                                            <td className="text-left zero-dark-grey">{device.last_access}</td>
                                            <td className="text-left zero-dark-grey">{statusFormatter(device)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            )}
                            {!loading && kioskSessions.length === 0 && (
                                <tbody style={{ borderTop: "0px" }}>
                                    <tr>
                                        <td
                                            className="react-bs-table-no-data"
                                            colSpan={6}
                                            style={{ color: "var(--zero-dark-grey)" }}
                                        >
                                            No kiosks.
                                        </td>
                                    </tr>
                                </tbody>
                            )}
                        </table>
                    </div>
                </div>

                <div style={{ margin: "0 auto", maxWidth: "800px", paddingLeft: "5px" }}>
                    <p
                        id="postidnumber"
                        className="text-md text-main text-bold pad-top text-center"
                        style={{ color: "#BCBABC" }}
                    >
                        Note: To protect your account, we will automatically delete any kiosks that have been inactive
                        for 90 days.
                    </p>
                </div>
            </div>
        </div>
    );
}

/**
 * @param {{
 *  kioskActivationKey: KioskActivationKey,
 *  onEdit: (kioskActivationKey: KioskActivationKey) => void,
 *  onStatusUpdate: (kioskActivationKey: KioskActivationKey) => void
 * }} props
 */
function KioskActivationLinkDetails({ kioskActivationKey, onEdit, onStatusUpdate }) {
    const link = getKioskActivationLink(kioskActivationKey);

    return (
        <div className="flex flex-col gap-sm">
            <div>
                <strong>Status:</strong>{" "}
                {kioskActivationKey.deactivated ? (
                    <Tag style={{ color: "#505050" }}>Disabled</Tag>
                ) : (
                    <Tag color="green" style={{ borderColor: "#52c41a" }}>
                        Enabled
                    </Tag>
                )}{" "}
                <span className="blue-link mar-lft-10" onClick={() => onStatusUpdate(kioskActivationKey)}>
                    Change status
                </span>
            </div>
            <div>
                <strong>Requires Code:</strong> {kioskActivationKey.requires_code ? "Yes" : "No"}{" "}
                <span className="blue-link mar-lft-10" onClick={() => onEdit(kioskActivationKey)}>
                    Change code
                </span>
            </div>
            <div>
                <strong>Created By:</strong> {kioskActivationKey.created_by?.full_name ?? "Unknown User"}
            </div>
            <div>
                <strong>Created On:</strong> {dateFormatterNoTime(kioskActivationKey.created_at)}
            </div>
            <div>
                <strong>Last Activity:</strong>{" "}
                {kioskActivationKey.last_activity_at
                    ? dateFormatterNoTime(kioskActivationKey.last_activity_at)
                    : "None"}
            </div>

            <QRCode link={link} showCopyAction downloadTitle="ZeroKioskActivationLink" />
        </div>
    );
}
